import { Star, Phone, CalendarCheck, ShieldCheck, Clock, Instagram, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CLINIC } from "@/lib/clinic";
import { buildWhatsAppLink } from "@/components/site/WhatsApp";

const HERO_WORDS = ["Your", "best", "smile", "awaits"];

const MARQUEE_ITEMS = [
  "Painless Root Canals",
  "Same-Day Appointments",
  "Digital X-Ray",
  "Clear Aligners",
  "Kid-Friendly Care",
  "Implants & Veneers",
  "Since 2014",
];

export default function Hero({ waNumber }) {
  const waLink = buildWhatsAppLink(waNumber || CLINIC.phoneDial);
  return (
    <section
      id="top"
      data-testid="section-hero"
      className="relative pt-28 md:pt-32 pb-14 md:pb-16 overflow-hidden"
    >
      {/* Ambient background shapes */}
      <div aria-hidden className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#D4B27E]/25 blur-3xl float-slow" />
      <div aria-hidden className="absolute top-40 -left-20 w-[420px] h-[420px] rounded-full bg-[#2D4A3E]/10 blur-3xl" style={{ animationDelay: "2s" }} />

      <div className="container-x relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Copy */}
        <div className="lg:col-span-6">
          <div
            data-testid="hero-rating-badge"
            className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-2 soft-shadow fade-up"
          >
            <div className="flex items-center gap-0.5 text-[#F59E0B]">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />
              ))}
            </div>
            <span className="text-sm text-[#1C221F] font-medium">
              {CLINIC.rating} · {CLINIC.reviewCount}+ Google reviews
            </span>
          </div>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C221F] leading-[1.02] word-reveal">
            {HERO_WORDS.map((w, i) => (
              <span
                key={i}
                style={{ animationDelay: `${120 + i * 90}ms` }}
                className="mr-3"
              >
                {w === "awaits" ? (
                  <span className="relative inline-block">
                    <span className="relative z-10">{w}</span>
                    <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#D4B27E]/50 -z-0 rounded-sm" aria-hidden />
                  </span>
                ) : (
                  w
                )}
              </span>
            ))}
            <br />
            <span style={{ animationDelay: `520ms` }} className="text-[#2D4A3E]">
              in Ratia.
            </span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-[#57605B] max-w-xl fade-up" style={{ animationDelay: "700ms" }}>
            {CLINIC.short} A family-run clinic trusted since 2014 —
            gentle check-ups, full smile makeovers, everything in between.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "820ms" }}>
            <Button
              asChild
              size="lg"
              className="rounded-full bg-[#25D366] hover:bg-[#1FBA5A] text-white px-6 h-12 shadow-[0_10px_30px_-10px_rgba(37,211,102,0.55)]"
            >
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="btn-whatsapp-hero">
                <MessageCircle className="w-5 h-5 mr-2" strokeWidth={2} />
                Book on WhatsApp
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              className="rounded-full bg-[#2D4A3E] hover:bg-[#21362D] text-white px-6 h-12 shadow-[0_10px_30px_-10px_rgba(45,74,62,0.55)]"
            >
              <a href="#contact" data-testid="btn-book-hero">
                <CalendarCheck className="w-5 h-5 mr-2" strokeWidth={1.75} />
                Book Appointment
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-[#2D4A3E]/25 text-[#2D4A3E] hover:bg-[#2D4A3E]/5 px-6 h-12"
            >
              <a href={`tel:${CLINIC.phoneDial}`} data-testid="btn-call-hero">
                <Phone className="w-5 h-5 mr-2" strokeWidth={1.75} />
                Call {CLINIC.phoneDisplay}
              </a>
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[#57605B] fade-up" style={{ animationDelay: "920ms" }}>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D4A3E]" strokeWidth={1.75} />Sterilised, safe clinic</span>
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#2D4A3E]" strokeWidth={1.75} />Open till 7:00 PM</span>
            <span className="inline-flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#2D4A3E]" strokeWidth={1.75} />Same-day appointments</span>
          </div>
        </div>

        {/* Image collage */}
        <div className="lg:col-span-6 fade-up" style={{ animationDelay: "180ms" }}>
          <div className="relative">
            <div className="absolute -inset-4 bg-[#2D4A3E]/5 rounded-[2rem] -rotate-2" aria-hidden />
            <div className="relative rounded-[2rem] overflow-hidden soft-shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1607613009820-a29f7bb81c04?auto=format&fit=crop&w=1400&q=80"
                alt="Family enjoying gentle dental care"
                className="w-full h-[480px] object-cover object-center"
              />
            </div>

            {/* Instagram badge — top right */}
            <a
              href={CLINIC.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-instagram-hero"
              className="absolute -top-3 -right-3 bg-white rounded-full pl-2 pr-4 py-2 soft-shadow-lg border border-stone-100 flex items-center gap-2 hover:bg-[#D4B27E]/10 transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4B27E] to-[#2D4A3E] flex items-center justify-center text-white">
                <Instagram className="w-4 h-4" strokeWidth={1.75} />
              </span>
              <span className="text-xs">
                <span className="block text-[#57605B] leading-tight">Follow us</span>
                <span className="block text-[#1C221F] font-medium leading-tight">{CLINIC.instagramHandle}</span>
              </span>
            </a>

            {/* Floating stat cards */}
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl p-4 soft-shadow-lg border border-stone-100 max-w-[240px] float-slow" style={{ animationDelay: "600ms" }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#2D4A3E] text-white flex items-center justify-center font-display font-bold text-xs">
                  50k+
                </div>
                <div>
                  <div className="text-sm font-medium text-[#1C221F]">Happy patients</div>
                  <div className="text-xs text-[#57605B]">across Ratia & Fatehabad</div>
                </div>
              </div>
            </div>

            <div className="absolute top-6 -left-8 bg-white rounded-2xl px-4 py-3 soft-shadow-lg border border-stone-100 hidden sm:block float-slow" style={{ animationDelay: "1200ms" }}>
              <div className="text-[10px] uppercase tracking-widest text-[#57605B]">Est.</div>
              <div className="font-display text-xl font-bold text-[#2D4A3E]">2014</div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee stat bar */}
      <div className="mt-16 md:mt-24 relative">
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FAFAF9] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#FAFAF9] to-transparent z-10 pointer-events-none" />
        <div className="flex overflow-hidden py-4 border-y border-stone-200 bg-white/40">
          <div className="flex ticker-tape w-max gap-14 items-center pr-14">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
              <div key={i} className="flex items-center gap-4 shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4B27E]" />
                <span className="font-display text-lg text-[#1C221F]/70">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
